import { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMyTaskCountersBadgeQuery } from 'core/graphql';
import { userStateVar } from 'core/state/userState';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { BADGE_POLL_INTERVAL_MS } from '../Communications/useGroupCommsIndex';
import { canReadTaskCounters } from './taskCounterQueries';

export interface IMyTaskCounters {
  overdue: number;
}

export interface IUseMyTaskCounters {
  /** `null` — роли счётчик не положен или ответа ещё нет. */
  counters: IMyTaskCounters | null;
  loading: boolean;
}

/**
 * ТЗ 0129 — бейдж «Таск-трекер» в меню: просроченные задачи пользователя (в
 * пределах видимых ему задач — продавцу только свои).
 *
 * `cache-and-network` + опрос раз в 60 с только при видимой вкладке. После
 * действий с задачами перечитывается документом (`taskCounterRefetch()`).
 */
export const useMyTaskCounters = (): IUseMyTaskCounters => {
  const roles = useReactiveVar(userStateVar).me?.roles ?? [];
  const allowed = canReadTaskCounters(roles);

  const {
    data,
    loading,
    startPolling,
    stopPolling,
  } = useMyTaskCountersBadgeQuery({
    skip: !allowed,
    fetchPolicy: 'cache-and-network',
  });
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    BADGE_POLL_INTERVAL_MS,
    allowed,
  );

  const raw = data?.myTaskCounters;
  const counters = useMemo<IMyTaskCounters | null>(
    () => (allowed && raw ? { overdue: raw.overdue } : null),
    [allowed, raw],
  );

  return { counters, loading: allowed && loading && !raw };
};
